import { useEffect, useEffectEvent, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { RollingNumber } from '../RollingNumber';
import { LONG_NIGHT } from '../../../campaign';
import { BrokerEyes, Candle } from './Broker';

/**
 * The run's heads-up pieces (docs/LONG_NIGHT_ART_DIRECTION.md §12.12, §9.2, §11.16): brass as a
 * small stack of tokens with its amount rolling, and the interest it will earn beside it in
 * dimmer brass; the title card set alone between Nights; and scenes rising out of the dark.
 */

/** Brass in the HUD: tokens, the amount in brass light, interest beside it. In debt, rose. */
export function BrassHud({
  brass,
  interest,
  reduceMotion = false,
  onClick,
}: {
  brass: number;
  /** What it will earn at the next table ("+4"). */
  interest?: number;
  reduceMotion?: boolean;
  onClick?: () => void;
}) {
  const debt = brass < 0;
  const body = (
    <>
      <span className="hud-tokens" aria-hidden="true">
        <CoinStack />
      </span>
      <span className="hud-amount">
        {debt && '−'}¤
        <RollingNumber value={Math.abs(brass)} reduceMotion={reduceMotion} ms={600} />
      </span>
      {interest !== undefined && interest > 0 && (
        <span className="hud-interest" title={t('night.interestNext', { n: interest })}>
          +{interest}
        </span>
      )}
    </>
  );
  const label = t('ledger.openLabel', { n: brass });
  return onClick ? (
    <button
      type="button"
      className={`hud-brass ${debt ? 'is-debt' : ''}`}
      onClick={onClick}
      aria-label={label}
    >
      {body}
    </button>
  ) : (
    <span className={`hud-brass ${debt ? 'is-debt' : ''}`} aria-label={label}>
      {body}
    </span>
  );
}

/** The scene label beside the candle: "NIGHT II · TERM III". */
export function NightLabel({ night, children }: { night: number; children: ReactNode }) {
  return (
    <span className="hud-night">
      <Candle night={night} size={24} />
      <span>{children}</span>
    </span>
  );
}

/**
 * Between Nights (§11.16): through the dark, held a little longer; the candle burns down a
 * notch; then the Night's title, alone and centered, for 1.6 s before the board rises. A tap
 * goes straight on. Closing Time (§11.17) is held for 1.2 s of black and silence first, and once
 * the title is set two points of light open above it: his eyes.
 */
export function NightTitleCard({
  title,
  night,
  still,
  onDone,
}: {
  title: string;
  night: number;
  still: boolean;
  onDone: () => void;
}) {
  const closing = night === LONG_NIGHT.NIGHTS + 1;
  const [shown, setShown] = useState(false);
  const [eyes, setEyes] = useState(false);
  const done = useEffectEvent(onDone);
  useEffect(() => {
    const dark = still ? 0 : closing ? 1200 : 900;
    const hold = closing ? 2600 : 1600;
    const a = setTimeout(() => setShown(true), dark);
    const e = closing ? setTimeout(() => setEyes(true), dark + 1100) : undefined;
    const b = setTimeout(done, dark + hold + (still ? 0 : 700));
    return () => {
      clearTimeout(a);
      clearTimeout(e);
      clearTimeout(b);
    };
  }, [still, closing]);
  return (
    <div
      className={`night-card ${shown ? 'is-shown' : ''} ${closing ? 'is-closing' : ''}`}
      onClick={onDone}
      role="status"
    >
      {closing && <BrokerEyes open={eyes} still={still} />}
      <Candle night={night} size={44} />
      <h2 className="night-card-title">{title}</h2>
    </div>
  );
}

/** A scene rising out of the dark (Through the dark, §9.2): a held breath, then up into the light. */
export function Rise({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div key={id} className="rise">
      {children}
    </div>
  );
}

/** Three brass tokens, stacked: pixel art, 8 × 9. */
export function CoinStack() {
  const coin = (y: number) => `M1 ${y}h6v1h-6z` + `M0 ${y + 1}h8v1h-8z`;
  return (
    <svg viewBox="0 0 8 9" shapeRendering="crispEdges">
      {[6, 3, 0].map((y) => (
        <g key={y}>
          <path fill="#c8902a" d={coin(y)} />
          <path fill="#ffe08a" d={`M2 ${y}h4v1h-4z`} />
          <path fill="#6e4a1e" d={`M0 ${y + 2}h8v1h-8z`} />
        </g>
      ))}
    </svg>
  );
}
